import React, { useState, useEffect } from 'react';
import { 
  saveAIRecommendations, 
  getAIRecommendations, 
  deleteAIRecommendations 
} from '../../lib/aiRecommendationsService';
import { supabase } from '../../lib/supabase';
import { Trash2, Plus, Save, RefreshCw, AlertCircle } from 'lucide-react';

interface AIRecommendationsManagerProps {
  prospectId: string;
  onUpdate?: () => void;
}

const AIRecommendationsManager: React.FC<AIRecommendationsManagerProps> = ({ 
  prospectId,
  onUpdate
}) => {
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newRecommendation, setNewRecommendation] = useState('');
  const [prospectDetails, setProspectDetails] = useState<Record<string, unknown> | null>(null);

  // Charger les données du prospect et ses recommandations
  useEffect(() => {
    const loadData = async () => {
      if (!prospectId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Charger les détails du prospect
        const { data: prospectData, error: prospectError } = await supabase
          .from('prospects')
          .select('prenom, nom, email, site_url, metadata')
          .eq('id', prospectId)
          .single();
          
        if (prospectError) throw prospectError;
        setProspectDetails(prospectData);
        
        // Charger les recommandations
        const recs = await getAIRecommendations(prospectId);
        if (recs) {
          setRecommendations(recs);
        } else {
          setRecommendations([]);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Impossible de charger les recommandations. Veuillez réessayer.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [prospectId]);

  // Ajouter une recommandation
  const handleAddRecommendation = () => {
    if (!newRecommendation.trim()) return;
    
    setRecommendations([...recommendations, newRecommendation.trim()]);
    setNewRecommendation('');
  };

  // Supprimer une recommandation
  const handleDeleteRecommendation = (index: number) => {
    const updatedRecs = [...recommendations];
    updatedRecs.splice(index, 1);
    setRecommendations(updatedRecs);
  };

  // Éditer une recommandation
  const handleEditRecommendation = (index: number, value: string) => {
    const updatedRecs = [...recommendations];
    updatedRecs[index] = value;
    setRecommendations(updatedRecs);
  };

  // Sauvegarder les recommandations
  const handleSave = async () => {
    if (!prospectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await saveAIRecommendations(prospectId, recommendations);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des recommandations:', err);
      setError('Impossible de sauvegarder les recommandations. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Supprimer toutes les recommandations
  const handleDeleteAll = async () => {
    if (!prospectId || !window.confirm('Êtes-vous sûr de vouloir supprimer toutes les recommandations?')) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await deleteAIRecommendations(prospectId);
      setRecommendations([]);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Erreur lors de la suppression des recommandations:', err);
      setError('Impossible de supprimer les recommandations. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">
          Gestion des Recommandations AI
        </h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Modifier
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              {isLoading ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
              Enregistrer
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Tout supprimer
            </button>
          </div>
        )}
      </div>
      
      {/* Détails du prospect */}
      {prospectDetails && (
        <div className="mb-4 p-4 bg-gray-50 rounded-md">
          <h3 className="font-medium text-gray-700 mb-2">Prospect: {prospectDetails.prenom} {prospectDetails.nom}</h3>
          <p className="text-sm text-gray-600">Email: {prospectDetails.email}</p>
          <p className="text-sm text-gray-600">Site web: {prospectDetails.site_url || 'Non spécifié'}</p>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}
      
      {/* Liste des recommandations */}
      <div className="mb-6">
        <h3 className="font-medium text-gray-700 mb-3">Recommandations actuelles:</h3>
        
        {isLoading && recommendations.length === 0 ? (
          <div className="py-8 flex justify-center">
            <RefreshCw className="animate-spin h-8 w-8 text-blue-500" />
          </div>
        ) : recommendations.length === 0 ? (
          <p className="text-gray-500 italic">Aucune recommandation disponible.</p>
        ) : (
          <ul className="space-y-3">
            {recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-purple-100 text-purple-700 font-medium mr-3 mt-1">
                  {index + 1}
                </span>
                {isEditing ? (
                  <div className="flex-1 flex">
                    <input
                      type="text"
                      value={rec}
                      onChange={(e) => handleEditRecommendation(index, e.target.value)}
                      className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => handleDeleteRecommendation(index)}
                      className="bg-red-100 text-red-600 p-2 rounded-r-md hover:bg-red-200"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <p className="flex-1 text-gray-700">{rec}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Formulaire d'ajout */}
      {isEditing && (
        <div className="mt-4 border-t pt-4">
          <h3 className="font-medium text-gray-700 mb-3">Ajouter une recommandation:</h3>
          <div className="flex">
            <input
              type="text"
              value={newRecommendation}
              onChange={(e) => setNewRecommendation(e.target.value)}
              placeholder="Saisir une nouvelle recommandation"
              className="flex-1 border border-gray-300 rounded-l-md px-3 py-2"
            />
            <button
              onClick={handleAddRecommendation}
              disabled={!newRecommendation.trim()}
              className="bg-purple-600 text-white px-4 py-2 rounded-r-md hover:bg-purple-700 disabled:bg-purple-300 flex items-center"
            >
              <Plus size={18} className="mr-1" />
              Ajouter
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIRecommendationsManager;
